/**
 * Quản lý đơn vị điều tra (Doc/06) — chỉ admin hệ thống.
 *
 * Nhiều đơn vị dùng chung một hệ thống: chung admin, chung bộ câu hỏi và ngưỡng đối
 * chiếu, nhưng **danh sách điều tra viên, nông hộ và phiếu thì tách biệt** — không
 * đơn vị nào thấy dữ liệu của đơn vị khác.
 *
 * Ba điều màn hình này cố ý KHÔNG cho làm:
 *
 *  - **Không đổi mã đơn vị.** Mã đã nằm trong các file xuất và dòng log đã phát sinh;
 *    đổi nó là làm hai file xuất của cùng một đơn vị đọc ra thành hai đơn vị khác nhau.
 *  - **Không xoá đơn vị.** Cùng lý do không có `DELETE /users/:id`: phiếu và hộ tham
 *    chiếu tới nó. Khoá (`isActive = false`) mới là thao tác đúng — người của đơn vị bị
 *    khoá không đăng nhập được nữa, dữ liệu vẫn nguyên vẹn.
 *  - **Không chuyển người/hộ giữa hai đơn vị.** Lịch sử phiếu phải nằm lại đơn vị đã
 *    thực hiện.
 */
import * as api from '../api-client.js';
import { esc, toast, formatDateTime } from '../ui/util.js';
import { openFormDialog } from '../ui/form-dialog.js';

export async function renderInstances(container) {
  const me = api.getCurrentUser();
  if (!me || me.role !== 'admin') {
    container.innerHTML = `<div class="empty-state"><span class="ms ms-xl">lock</span>
      <p>Chỉ quản trị hệ thống mới được quản lý đơn vị điều tra.</p></div>`;
    return;
  }

  container.innerHTML = `
    <div class="page-head">
      <div>
        <h1>Đơn vị điều tra</h1>
        <p>Mỗi đơn vị có danh sách điều tra viên và nông hộ riêng, <b>tách biệt hoàn toàn</b>
           với đơn vị khác. Bộ câu hỏi, mục đối chiếu và ngưỡng dùng chung toàn hệ thống.</p>
      </div>
      <div class="page-head-actions">
        <button class="btn" id="iNewBtn"><span class="ms">add_business</span>Tạo đơn vị</button>
      </div>
    </div>
    <div id="iList"></div>`;

  const listHost = container.querySelector('#iList');
  let instances = [];
  /** id đơn vị -> số liệu tổng quan. Nạp sau danh sách để bảng hiện ra ngay. */
  const summaries = new Map();
  let regionCatalog = null;

  async function load() {
    listHost.innerHTML = `<div class="empty-state">Đang tải danh sách đơn vị...</div>`;
    try {
      instances = await api.instances.list();
      renderList();
      await loadSummaries();
    } catch (err) {
      listHost.innerHTML = `<div class="empty-state"><span class="ms ms-xl">error</span>
        <p>${esc(err.message)}</p></div>`;
    }
  }

  /**
   * Số liệu từng đơn vị nạp SAU khi bảng đã hiện.
   *
   * Chờ đủ N request rồi mới vẽ gì cả là màn hình trắng vài giây với 5–6 đơn vị, mà
   * phần chậm lại là phần ít quan trọng nhất — tên và trạng thái đơn vị đã có sẵn
   * trong danh sách.
   */
  async function loadSummaries() {
    await Promise.all(
      instances.map(async (inst) => {
        try {
          summaries.set(inst.id, await api.instances.summary(inst.id));
        } catch {
          // Một đơn vị lỗi không được làm trắng cả bảng — ô số liệu để dấu gạch.
          summaries.set(inst.id, null);
        }
      }),
    );
    renderList();
  }

  const soLieu = (inst, key) => {
    const s = summaries.get(inst.id);
    if (s === undefined) return '<span class="hint-text">…</span>';
    if (s === null) return '—';
    return String(s[key]);
  };

  function renderList() {
    if (!instances.length) {
      listHost.innerHTML = `<div class="empty-state"><span class="ms ms-xl">domain_disabled</span>
        <p>Chưa có đơn vị điều tra nào.</p></div>`;
      return;
    }
    listHost.innerHTML = `
      <div class="card">
        <table class="list-table">
          <thead><tr>
            <th>Mã</th><th>Tên đơn vị</th><th>Địa bàn</th><th>Tài khoản</th>
            <th>Nông hộ</th><th>Vườn</th><th>Phiếu</th><th>Trạng thái</th><th>Tạo lúc</th><th></th>
          </tr></thead>
          <tbody>
            ${instances
              .map(
                (inst) => `<tr class="${inst.isActive ? '' : 'user-locked'}">
                  <td><code>${esc(inst.code)}</code></td>
                  <td>${esc(inst.name)}</td>
                  <td>${soLieu(inst, 'regions')}</td>
                  <td>${soLieu(inst, 'users')}</td>
                  <td>${soLieu(inst, 'households')}</td>
                  <td>${soLieu(inst, 'plots')}</td>
                  <td>${soLieu(inst, 'submissions')}</td>
                  <td><span class="badge ${inst.isActive ? 'user-active' : 'user-inactive'}">${
                    inst.isActive ? 'Hoạt động' : 'Đã khoá'
                  }</span></td>
                  <td>${esc(formatDateTime(inst.createdAt))}</td>
                  <td class="user-actions">
                    <button class="btn btn-text sm" data-act="edit" data-id="${esc(inst.id)}">Sửa tên</button>
                    <button class="btn btn-text sm" data-act="regions" data-id="${esc(inst.id)}">Địa bàn</button>
                    <button class="btn btn-text sm ${inst.isActive ? 'danger' : ''}" data-act="toggle" data-id="${esc(inst.id)}">${
                      inst.isActive ? 'Khoá' : 'Mở khoá'
                    }</button>
                  </td>
                </tr>`,
              )
              .join('')}
          </tbody>
        </table>
      </div>`;

    listHost.querySelectorAll('button[data-act]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const inst = instances.find((i) => i.id === btn.dataset.id);
        if (!inst) return;
        if (btn.dataset.act === 'edit') openEditDialog(inst);
        if (btn.dataset.act === 'regions') openRegionsDialog(inst);
        if (btn.dataset.act === 'toggle') toggleActive(inst);
      });
    });
  }

  function openCreateDialog() {
    openFormDialog({
      title: 'Tạo đơn vị điều tra',
      submitLabel: 'Tạo đơn vị',
      bodyHtml: `
        <div class="form-row">
          <label for="iCode">Mã đơn vị</label>
          <input id="iCode" name="code" required maxlength="16" placeholder="VD: TQC" style="text-transform:uppercase">
          <p class="hint-text">Chữ in không dấu, số và dấu gạch ngang. Mã này đi vào cột
             <code>instance_code</code> của file xuất và <b>không đổi được về sau</b>.</p>
        </div>
        <div class="form-row">
          <label for="iName">Tên đơn vị</label>
          <input id="iName" name="name" required maxlength="120" placeholder="VD: Công ty TQC">
        </div>
        <p class="hint-text">Sau khi tạo: gán <b>địa bàn phụ trách</b>, rồi tạo tài khoản
           <b>Quản trị đơn vị</b> ở màn Tài khoản để họ tự quản lý điều tra viên.</p>`,
      onSubmit: async (form) => {
        const code = form.code.value.trim().toUpperCase();
        const name = form.name.value.trim();
        if (code.length < 2) return 'Mã đơn vị phải có ít nhất 2 ký tự.';
        if (name.length < 3) return 'Tên đơn vị phải có ít nhất 3 ký tự.';
        try {
          await api.instances.create({ code, name });
        } catch (err) {
          return err.message;
        }
        toast('Đã tạo đơn vị điều tra');
        await load();
      },
    });
  }

  function openEditDialog(inst) {
    openFormDialog({
      title: `Sửa đơn vị "${inst.name}"`,
      bodyHtml: `
        <div class="form-row">
          <label>Mã đơn vị</label>
          <input value="${esc(inst.code)}" disabled>
          <p class="hint-text">Mã không đổi được: nó đã nằm trong các file xuất và dòng log
             đã phát sinh.</p>
        </div>
        <div class="form-row">
          <label for="iName2">Tên đơn vị</label>
          <input id="iName2" name="name" required maxlength="120" value="${esc(inst.name)}">
        </div>`,
      onSubmit: async (form) => {
        const name = form.name.value.trim();
        if (name.length < 3) return 'Tên đơn vị phải có ít nhất 3 ký tự.';
        try {
          await api.instances.update(inst.id, { name });
        } catch (err) {
          return err.message;
        }
        toast('Đã cập nhật đơn vị');
        await load();
      },
    });
  }

  async function toggleActive(inst) {
    const next = !inst.isActive;
    const warn = next
      ? `Mở khoá đơn vị "${inst.name}"?`
      : `Khoá đơn vị "${inst.name}"?\n\nMọi tài khoản thuộc đơn vị này sẽ không đăng nhập được nữa ` +
        `và phiên đang mở bị thu hồi. Dữ liệu vẫn được giữ nguyên.`;
    if (!window.confirm(warn)) return;
    try {
      await api.instances.update(inst.id, { isActive: next });
      toast(next ? 'Đã mở khoá đơn vị' : 'Đã khoá đơn vị và thu hồi phiên đăng nhập');
      await load();
    } catch (err) {
      toast('Không thực hiện được: ' + err.message);
    }
  }

  /**
   * Gán địa bàn phụ trách — danh sách checkbox theo cây Tỉnh → Xã.
   *
   * Không dùng `region-picker` (nó chọn MỘT địa bàn); ở đây là chọn nhiều, và thao tác
   * là *thay thế* toàn bộ danh sách chứ không phải thêm vào.
   */
  async function openRegionsDialog(inst) {
    try {
      regionCatalog = regionCatalog ?? (await api.admin.regions.list());
    } catch (err) {
      toast('Không tải được danh mục địa bàn: ' + err.message);
      return;
    }
    let daGan = [];
    try {
      daGan = await api.instances.regions(inst.id);
    } catch (err) {
      toast('Không tải được địa bàn của đơn vị: ' + err.message);
      return;
    }
    const dangChon = new Set(daGan.map((r) => r.id));

    const tinhs = regionCatalog.filter((r) => r.level === 'province');
    const xasCua = (tinhId) =>
      regionCatalog.filter((r) => r.level !== 'province' && r.parentId === tinhId);

    const bodyHtml = `
      <p class="hint-text">Đơn vị chỉ tạo được hộ ở những địa bàn được chọn ở đây. Hai đơn vị
         <b>được phép</b> cùng phụ trách một địa bàn — dữ liệu hộ của họ vẫn tách biệt.</p>
      <p class="hint-text">Tích vào <b>tỉnh</b> sẽ chọn luôn toàn bộ xã trực thuộc; bỏ tích tỉnh
         thì bỏ hết. Sau đó vẫn bỏ tích được từng xã lẻ.</p>
      <div class="form-row">
        <input type="search" data-role="region-filter" class="search-field"
               placeholder="Lọc theo tên tỉnh/xã..." aria-label="Lọc địa bàn">
      </div>
      <div class="region-multi" data-role="region-tree">
        ${tinhs
          .map(
            (t) => `
          <fieldset class="region-multi-group" data-name="${esc(t.name.toLowerCase())}">
            <legend>
              <label><input type="checkbox" data-kind="province" value="${esc(t.id)}"
                ${dangChon.has(t.id) ? 'checked' : ''}> ${esc(t.name)}
                <span class="region-multi-count" data-role="count"></span></label>
            </legend>
            <div class="region-multi-children">
              ${xasCua(t.id)
                .map(
                  (x) => `<label data-name="${esc(x.name.toLowerCase())}">
                    <input type="checkbox" data-kind="commune" value="${esc(x.id)}"
                      ${dangChon.has(x.id) ? 'checked' : ''}> ${esc(x.name)}</label>`,
                )
                .join('')}
            </div>
          </fieldset>`,
          )
          .join('')}
      </div>`;

    openFormDialog({
      title: `Địa bàn phụ trách — ${inst.name}`,
      submitLabel: 'Lưu danh sách',
      bodyHtml,
      onMount: (form) => {
        // Tích tỉnh = tích toàn bộ xã trực thuộc.
        //
        // Không phải tiện tay: `assertRegionInInstance` so khớp ĐÚNG một region_id, không
        // nở ra theo cây. Gán mỗi ID tỉnh cho đơn vị là gán một thứ vô dụng — không hộ nào
        // ở xã bên dưới tạo được. Muốn phụ trách cả tỉnh thì phải có mặt từng xã trong
        // danh sách, nên ô tỉnh phải kéo theo các ô xã.
        //
        // Ô tỉnh vẫn là một địa bàn gán được thật (`assertRegionShape` cho phép gán
        // supervisor vào regionId cấp tỉnh, và nó cũng đòi tỉnh đó có trong danh sách của
        // đơn vị), nên bỏ tích một xã lẻ KHÔNG tự bỏ tích tỉnh — chỉ đổi số đếm bên cạnh.
        //
        // KHÔNG dùng `input.indeterminate` để báo "chọn dở dang": Chrome không cho ô
        // indeterminate khớp `:checked`, mà `onSubmit` gom danh sách bằng đúng selector đó
        // — ô tỉnh sẽ lặng lẽ rơi khỏi payload dù `checked === true`. Số đếm bên cạnh làm
        // đúng việc báo hiệu mà không đụng vào giá trị gửi lên.
        form.querySelectorAll('.region-multi-group').forEach((group) => {
          const oTinh = group.querySelector('input[data-kind="province"]');
          const oXas = [...group.querySelectorAll('input[data-kind="commune"]')];
          const soDem = group.querySelector('[data-role="count"]');
          const capNhatDem = () => {
            const chon = oXas.filter((x) => x.checked).length;
            soDem.textContent = oXas.length ? `${chon}/${oXas.length} xã` : '';
          };
          oTinh.addEventListener('change', () => {
            oXas.forEach((x) => {
              x.checked = oTinh.checked;
            });
            capNhatDem();
          });
          oXas.forEach((x) => x.addEventListener('change', capNhatDem));
          capNhatDem();
        });

        // Lọc tại chỗ: 226 xã là quá nhiều để cuộn tay, và tải lại danh mục cho mỗi
        // lần gõ là vô nghĩa khi cả danh mục đã nằm trong bộ nhớ.
        const filter = form.querySelector('[data-role="region-filter"]');
        filter.addEventListener('input', () => {
          const q = filter.value.trim().toLowerCase();
          form.querySelectorAll('.region-multi-group').forEach((group) => {
            const tenTinh = group.dataset.name || '';
            let hienNhom = !q || tenTinh.includes(q);
            group.querySelectorAll('.region-multi-children label').forEach((lab) => {
              const khop = !q || (lab.dataset.name || '').includes(q) || tenTinh.includes(q);
              lab.hidden = !khop;
              if (khop) hienNhom = true;
            });
            group.hidden = !hienNhom;
          });
        });
      },
      onSubmit: async (form) => {
        const regionIds = [...form.querySelectorAll('input[type="checkbox"]:checked')].map(
          (cb) => cb.value,
        );
        try {
          await api.instances.setRegions(inst.id, regionIds);
        } catch (err) {
          // Bỏ một địa bàn còn hộ đang thuộc về -> 409 kèm số hộ. Hiện nguyên văn:
          // thông báo của máy chủ nói rõ còn bao nhiêu hộ, câu tự viết ở đây thì không.
          return err.message;
        }
        toast(`Đã gán ${regionIds.length} địa bàn cho đơn vị`);
        await load();
      },
    });
  }

  container.querySelector('#iNewBtn').addEventListener('click', openCreateDialog);
  await load();
  return () => {};
}
